import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { analytics } from '@heycatch/sdk';
import { BACKEND_URL as APP_BACKEND_URL } from '@/lib/backendUrl';

const BACKEND_URL = APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

// ──────────────────────────────────────────────────────────────────────────
// SECURITY: The JWT used to live in localStorage which is readable by any
// script (XSS = token theft). We now store the token in two layers:
//   1) httpOnly `access_token` cookie set by the backend (primary, XSS-safe)
//   2) in-memory variable below (so existing components that still send
//      `Authorization: Bearer ${token}` keep working without re-architecting)
//
// The in-memory token is LOST on a hard refresh — that's fine, because the
// cookie is still valid and `/api/auth/me` will re-establish the session on
// page load. Components that send the explicit Authorization header should
// either migrate to the `api` axios instance (cookies sent automatically) or
// add `credentials: 'include'` to their fetch() calls.
// ──────────────────────────────────────────────────────────────────────────

let _inMemoryToken = null;

// One-time migration from localStorage so we don't log existing users out.
// The token is moved to memory; the localStorage copy is then wiped.
function _migrateLegacyToken() {
  try {
    const legacy = localStorage.getItem('token');
    if (legacy) {
      _inMemoryToken = legacy;
      localStorage.removeItem('token');
    }
  } catch (e) {
    console.warn('AuthContext: legacy token migration skipped', e);
  }
}
_migrateLegacyToken();

export function getAuthToken() {
  return _inMemoryToken;
}

function setAuthToken(token) {
  _inMemoryToken = token || null;
}

export const api = axios.create({
  baseURL: API,
  // Send cookies (httpOnly access_token) on every request so the session
  // works even if the in-memory token has been cleared (hard refresh).
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  // Legacy fallback: if we still hold an in-memory JWT, also send it as a
  // Bearer header. The backend accepts either. Remove this branch entirely
  // once all surfaces (uploads, etc.) have been migrated to cookie-only auth.
  if (_inMemoryToken) {
    config.headers.Authorization = `Bearer ${_inMemoryToken}`;
  }
  return config;
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      // Cookies are sent automatically; if a valid access_token cookie exists,
      // /auth/me will return the user. If not (and no in-memory token), 401.
      const res = await api.get('/auth/me');
      setUser(res.data);
    } catch (err) {
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        setAuthToken(null);
        setUser(null);
      }
      // On network errors, keep the user state — don't log them out
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // HeyCatch identity: every sign-in path (password, MFA, register, Google, cookie restore) lands here once per member.
  const identityId = user?.user_id;
  const identityEmail = user?.email;
  const identityName = user?.name;
  useEffect(() => {
    if (identityId) analytics.setIdentity(identityId, { email: identityEmail, name: identityName });
  }, [identityId, identityEmail, identityName]);

  const loginWithJWT = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    // MFA-enabled accounts return a challenge token instead of a session JWT.
    if (res.data?.mfa_required) {
      return { mfa_required: true, challenge_token: res.data.challenge_token, partial_user: res.data.user };
    }
    setAuthToken(res.data.token);
    setUser(res.data.user);
    // Dispatch event for TranslationContext to sync language
    window.dispatchEvent(new Event('auth-changed'));
    return res.data.user;
  };

  const verifyMfaLogin = async (challenge_token, code) => {
    const res = await api.post('/mfa/verify-login', { challenge_token, code });
    setAuthToken(res.data.token);
    setUser(res.data.user);
    window.dispatchEvent(new Event('auth-changed'));
    return res.data.user;
  };

  const register = async (email, password, name, extra = {}) => {
    const res = await api.post('/auth/register', { email, password, name, ...extra });
    setAuthToken(res.data.token);
    setUser(res.data.user);
    // The account exists the moment this resolves — identify first so the business event attaches to the right person.
    analytics.setIdentity(res.data.user.user_id, { email: res.data.user.email, name: res.data.user.name });
    analytics.trackEvent('signup_completed', { method: 'email' });
    // Dispatch event for TranslationContext to sync language
    window.dispatchEvent(new Event('auth-changed'));
    return res.data.user;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {
      console.error('AuthContext: logout request failed', e);
    }
    setAuthToken(null);
    setUser(null);
    analytics.resetIdentity(); // the next visitor on this browser starts anonymous
  };

  const setUserData = (userData) => setUser(userData);

  // Memoized so consumers only re-render when auth state actually changes,
  // not on every incidental AuthProvider render.
  const value = useMemo(
    () => ({ user, loading, loginWithJWT, verifyMfaLogin, register, loginWithGoogle, logout, setUserData, checkAuth, get token() { return _inMemoryToken; } }),
    [user, loading, checkAuth],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
